import { useNavigate } from 'react-router-dom';
import { I18n } from 'src/modules/common/components/I18n/I18n.tsx';
import { MESSAGES } from 'src/modules/common/constants.ts';
import { useCallback, useContext, useLayoutEffect } from 'react';
import { MainLayout } from 'src/modules/common/layouts/MainLayout.tsx';
import { NavigationContainer } from 'src/modules/common/components/Navigation/NavigationContainer.tsx';
import { NavigationButton } from 'src/modules/common/components/Navigation/NavigationButton.tsx';
import { LocationStateContextType } from 'src/sensors/locationStateContext.ts';
import { RuntimeContext } from 'src/runtimeContext';
import { sortAccessories, sortAccessoriesBy } from 'src/modules/common/utils/sortAccessories.ts';
import { SensorsAccessoriesPageData } from 'src/sensors/accessories/SensorsAccessoriesPageMapping.ts';
import { MainLayoutHeader } from 'src/modules/common/components/MainLayoutHeader/MainLayoutHeader';
import { AccessoryGrid } from 'src/common/Accessory/AccessoryGrid';
import { Accessory } from 'src/common/Accessory/Accessory';
import { useLocationState } from 'src/sensors/useLocationState.ts';
import { SensorBreadcrumbs } from 'src/sensors/common/components/SensorBreadcrumbs/SensorBreadcrumbs';
import { PATHS } from 'src/sensors/common/types/Paths';
import { SelectionsButton } from 'src/projects/components/SelectionsButton.tsx';
import { parseRecommended } from 'src/modules/common/utils/parseRecommended.ts';
import { AccessoriesPipeConnectors } from 'src/common/AccessoriesPipeConnectors/AccessoriesPipeConnectors.tsx';
import { isNotFromBreadCrumb } from 'src/common/breadcrumbUtils.ts';
import { getPriceValue } from 'src/modules/checkout/getPriceValue.ts';
import { Price } from 'src/common/Price/Price.tsx';
import { ConfigContext } from 'src/configContext.ts';

export const SensorAccessoriesPage = ({ data }: { data: SensorsAccessoriesPageData }) => {
    const [locationState, setLocationState, searchParams] = useLocationState();
    const { isMobile, isRevitPlugin } = useContext(RuntimeContext);
    const { userSettings, currency } = useContext(ConfigContext);

    const navigate = useNavigate();

    const navigation = (
        <NavigationContainer isMobile={isMobile}>
            <NavigationButton
                type="forward"
                state={locationState}
                to={{
                    pathname: PATHS.Checkout,
                    search: searchParams.toString(),
                }}
                isMobile={isMobile}
            >
                <I18n messageId={MESSAGES.next}/>
            </NavigationButton>
        </NavigationContainer>
    );

    const resetHandler = useCallback(() => {
        setLocationState({
            ...locationState,
            selectedAccessories: [],
        });
    }, [locationState, setLocationState]);

    const {
        isRecommended,
        getQty,
    } = parseRecommended(data.accessories.sensor.connectors);

    const onChange = useCallback((label: string) => {
        let selectedAccessories: LocationStateContextType['locationState']['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({ ...locationState, selectedAccessories });
    }, [locationState, setLocationState, getQty]);

    const onPipeConnectorSelect = useCallback((label: string) => {
        let selectedAccessories: LocationStateContextType['locationState']['selectedAccessories'] = [];
        if (locationState.selectedAccessories.find((item) => item.label === label) !== undefined) {
            selectedAccessories = locationState.selectedAccessories.filter((item) => item.label !== label);
        } else {
            selectedAccessories = [...locationState.selectedAccessories, { label, qty: getQty(label) }];
        }
        setLocationState({ ...locationState, pipeConnectors: false, selectedAccessories });
    }, [locationState, setLocationState, getQty]);

    const onPipeConnectorsChange = useCallback((value: boolean) => {
        setLocationState({
            ...locationState,
            pipeConnectors: value,
            selectedAccessories: [
                ...locationState.selectedAccessories.filter(({ label }) => !isRecommended(label)),
                ...(value ? data.accessories.accessories
                    .filter(({ item }) => isRecommended(item.Label))
                    .map(({ item }) => ({ label: item.Label, qty: getQty(item.Label) }))
                    : []),
            ],
        });
    }, [setLocationState, locationState, data, isRecommended, getQty]);

    const pipeConnectors = data.accessories.accessories
        .filter(({ item }) => isRecommended(item.Label))
        .sort(sortAccessoriesBy(isRecommended));

    const accessories = data.accessories.accessories
        .filter(({ item }) => !isRecommended(item.Label));

    useLayoutEffect(() => {
        if (isNotFromBreadCrumb(searchParams) && pipeConnectors.length + accessories.length === 0) {
            navigate({
                pathname: PATHS.Checkout,
                search: searchParams.toString(),
            });
        }
    }, [accessories, navigate, pipeConnectors, searchParams]);

    const renderAccessory = (item: SensorsAccessoriesPageData['accessories']['accessories'][0]['item']) => {
        const price = getPriceValue(
            item,
            userSettings.shoppingCenter as keyof typeof item,
            currency,
        );
        return (
            <Accessory
                chip={isRecommended(item.Label) ? <I18n messageId={MESSAGES.recommended}/> : null}
                accessory={item}
                onSelect={() => onChange(item.Label)}
                selected={locationState.selectedAccessories.find(({ label }) => label === item.Label) !== undefined}
                key={`${item.Label}-${item.ID}`}
                price={(
                    <Price
                        price={price.value}
                        currency={price.currency}
                        locale={userSettings.currentLocale}
                    />
                )}
            />
        );
    };

    return (
        <div className="vss-sensors">
            <MainLayout>
                <MainLayoutHeader
                    titleId={MESSAGES.accessoriesPageTitle}
                    navigation={isMobile ? null : navigation}
                    breadcrumbs={(
                        <SensorBreadcrumbs
                            currentPage={PATHS.Accessories}
                            resetHandler={locationState.selectedAccessories ? resetHandler : undefined}
                            locationState={locationState}
                            searchParams={searchParams}
                        />
                    )}
                    selectionButton={!isRevitPlugin && (
                        <SelectionsButton
                            isMobile={isMobile}
                            searchParams={searchParams}
                            locationState={locationState}
                        />
                    )}
                />

                <div className="vss-accessories-page">
                    {pipeConnectors.length > 0 && (
                        <AccessoriesPipeConnectors
                            isMobile={isMobile}
                            isSelected={locationState.pipeConnectors}
                            selectedAccessories={locationState.selectedAccessories}
                            onSelect={onPipeConnectorSelect}
                            pipeConnectors={pipeConnectors}
                            onPipeConnectorsChange={onPipeConnectorsChange}
                            getQty={getQty}
                        />
                    )}
                    {accessories.length > 0 && (
                        <AccessoryGrid
                            title={MESSAGES.accessoriesPageSensorsTitle}
                            isMobile={isMobile}
                        >
                            {accessories
                                .slice()
                                .sort(sortAccessories)
                                .map(({ item }) => renderAccessory(item))}
                        </AccessoryGrid>
                    )}
                </div>
            </MainLayout>

            {isMobile && navigation}
        </div>
    );
};
